 
import React, {
  useCallback, useMemo,
} from 'react';
import {
  Box,
  WarningIcon,
} from '@common-fe/common-fe';

import { BlockWrapper } from '@/components';
import { useEmployerReactivationModalStore } from '@/modules/employer/components/EmployerReactivation/stores';
import { useCancelEmployerTerminationModalStore, useEmployerTerminationModalStore } from '@/modules/employer/components/EmployerTermination/store';
import { EmployerStatus } from '@/modules/employer/employer.constants';

import { ReactivationDangerZoneCard } from './ReactivationDangerZoneCard';
import { TerminationDangerZoneCard } from './TerminationDangerZoneCard';
  
  interface Props {
    employerId?: string;
    employerName?: string;
    employerStatus?: EmployerStatus;
    employerPath?: string;
    terminationDate?: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additionalAction?: (value?: any) => void;
  }

const DangerZone: React.FC<Props> = ({
  employerStatus,
  employerName,
  employerId,
  employerPath,
  terminationDate,
  additionalAction,
}) => {  
  const handleChangeVisibility = useEmployerTerminationModalStore((state) => state.handleChangeVisibility);
  const handleSetEmployerId = useEmployerTerminationModalStore((state) => state.handleSetEmployerId);
  const handleSetEmployerName = useEmployerTerminationModalStore((state) => state.handleSetEmployerName);
  const handleSetAdditionalAction = useEmployerTerminationModalStore((state) => state.handleSetAdditionalAction);
  const handleSetTerminationDate = useEmployerTerminationModalStore((state) => state.handleSetTerminationDate);
  const handleSetEditMode = useEmployerTerminationModalStore((state) => state.handleSetEditMode);

  const handleChangeVisibilityCancel = useCancelEmployerTerminationModalStore((state) => state.handleChangeVisibility);
  
  const handleSetReactivationEmployerId = useEmployerReactivationModalStore((state) => state.handleSetEmployerId);
  const handleSetReactivationEmployerName = useEmployerReactivationModalStore((state) => state.handleSetEmployerName);
  const handleSetReactivationEmployerPath = useEmployerReactivationModalStore((state) => state.handleSetEmployerPath);
  const handleSetReactivationAdditionalAction = useEmployerReactivationModalStore((state) => state.handleSetAdditionalAction);
  const handleChangeReactivationVisibility = useEmployerReactivationModalStore((state) => state.handleChangeVisibility);

  const currentTitle = useMemo(() => {
    if (employerStatus === EmployerStatus.Terminated || employerStatus === EmployerStatus.TerminationInProgress) {
      return `Reactivate ${employerName || 'Employer'}`;
    }
    if (employerStatus === EmployerStatus.PendingReactivation) {
      return 'Reactivation in Progress';
    }
    if (employerStatus === EmployerStatus.PendingTermination || terminationDate) {
      return 'Pending Termination';
    }
    return `Terminate ${employerName || 'Employer'}`;
  }, [employerName, employerStatus, terminationDate]);

  const description = useMemo(() => {
    if (employerStatus === EmployerStatus.PendingReactivation) {
      return `${employerName || 'Employer'} is being reactivated.\nThis can take a few moments to complete.`;
    }
    if (terminationDate || employerStatus === EmployerStatus.PendingTermination) {
      return `Terminate on ${terminationDate}`;
    }
    return undefined;
  }, [employerName, employerStatus, terminationDate]);


  const handleTerminateEmployer = useCallback(() => {
    handleSetEmployerName(employerName);
    handleSetEmployerId(employerId ? `${employerId}` : undefined);
    handleSetAdditionalAction(additionalAction);
    handleChangeVisibility(true);
    if (terminationDate) {
      handleSetTerminationDate(terminationDate);
      handleSetEditMode(true);
    }
  }, [
    handleSetEmployerName,
    employerName,
    handleSetEmployerId,
    employerId,
    handleSetAdditionalAction,
    additionalAction,
    handleChangeVisibility,
    terminationDate,
    handleSetTerminationDate,
    handleSetEditMode,
  ]);

  const handleCancelTermination = useCallback(() => {
    handleChangeVisibilityCancel(true);
    handleSetEmployerName(employerName);
    handleSetEmployerId(employerId ? `${employerId}` : undefined);
    handleSetAdditionalAction(additionalAction);
  }, [
    employerId,
    employerName,
    handleChangeVisibilityCancel,
    handleSetEmployerId,
    handleSetEmployerName,
    additionalAction,
    handleSetAdditionalAction,
  ]);

  const handleReactivateEmployer = useCallback(() => {
    handleSetReactivationEmployerId(employerId);
    handleSetReactivationEmployerName(employerName);
    handleSetReactivationEmployerPath(employerPath);
    handleSetReactivationAdditionalAction(additionalAction);
    handleChangeReactivationVisibility(true);
  }, [employerId, employerName, employerPath, additionalAction, handleSetReactivationEmployerId, handleSetReactivationEmployerName, handleSetReactivationEmployerPath, handleSetReactivationAdditionalAction, handleChangeReactivationVisibility]);

  const isReactivationMode = useMemo(() => {
    return employerStatus === EmployerStatus.Terminated || 
      employerStatus === EmployerStatus.TerminationInProgress || 
      employerStatus === EmployerStatus.PendingReactivation;
  }, [employerStatus]);

  return (
    <BlockWrapper
      moduleAnchorId="danger_zone"
      title="Danger Zone"
      icon={<WarningIcon />}
      blockIconColor="warning"
    >
      <Box direction="row" background={{ color: 'module' }}>
        {isReactivationMode ? (
          <ReactivationDangerZoneCard
            title={currentTitle}
            description={description}
            employerStatus={employerStatus}
            onReactivateEmployer={handleReactivateEmployer}
          />
        ) : (
          <TerminationDangerZoneCard
            title={currentTitle}
            description={description}
            onViewDetails={description ? handleTerminateEmployer : undefined}
            employerStatus={employerStatus}
            terminationDate={terminationDate}
            onCancelTermination={handleCancelTermination}
            onTerminateEmployer={handleTerminateEmployer}
          />
        )}
        <Box fill margin={{ left: 'spacing8' }} />
      </Box>
    </BlockWrapper>
  );
};
  
export default DangerZone;
  